/**
 * The interruption budget for components (the rules and the store are in
 * lib/interruption-budget). The bottom of the screen was shared by the
 * trial sheet, the first-visit install sheet and the feedback offer, and
 * without an owner they stacked (seen 2026-10-15: the trial card on top
 * of the feedback prompt on desktop, the install sheet on phones). Since
 * 2026-10-29 the slot is also a session budget: one offer per session,
 * none on its first page view, none over a result or a drawer.
 *
 *   - a floating prompt asks for its turn with `useInterruption` once its
 *     own rules pass, and renders only while the hook says so;
 *   - whatever nothing may cover (a result, a drawer, the cookie note)
 *     calls `useHoldInterruptions` while it is on screen;
 *   - the install card under a result asks with `useInlineTurn`;
 *   - a coach mark (a pulse and a tooltip on a control) asks with
 *     `useCoachMark`, below every offer (since 2026-10-31).
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type RefObject } from "react";
import {
  SERVER_SNAPSHOT, drop, getSnapshot, hold, spend, subscribe, want,
  type Floating, type Hint, type HoldKind, type Inline,
} from "@/lib/interruption-budget";

const getServerSnapshot = () => SERVER_SNAPSHOT;

/** A floating prompt asks for the screen while ``ready`` (its own rules
 *  passed and its own delay is over); true while it is its turn. The turn
 *  goes back the moment ``ready`` is false, and an offer loses it for as
 *  long as a hold lasts. */
export function useInterruption(who: Floating, ready: boolean): boolean {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  useEffect(() => {
    if (!ready) return undefined;
    want(who);
    return () => drop(who);
  }, [who, ready]);
  return ready && snap.owner === who;
}

/** While ``on``, no offer comes up: the caller is something the person
 *  has open (a result, an analysis on its way, a drawer; the team's reply
 *  waits for these too) or, with ``kind`` "cookie", the cookie note, which
 *  comes before every prompt. */
export function useHoldInterruptions(on = true, kind: HoldKind = "open"): void {
  useEffect(() => (on ? hold(kind) : undefined), [on, kind]);
}

/** An inline offer renders while this is true: the session has not been
 *  used by another prompt. It uses the session itself once half of
 *  ``ref`` has been on screen (scrolled past unseen, it costs nothing). */
export function useInlineTurn(who: Inline, ready: boolean, ref: RefObject<Element | null>): boolean {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const allowed = ready && (snap.spent === null || snap.spent === who);
  const taken = snap.spent === who;
  useEffect(() => {
    const el = ref.current;
    if (!allowed || taken || !el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      spend(who);
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      spend(who);
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [allowed, taken, who, ref]);
  return allowed;
}

/** A coach mark asks this long after it could show, so the offers due on
 *  the same page (the install sheet at once, the feedback card after
 *  1.5 s) have asked by then and come before it. */
const HINT_ASK_MS = 2_500;

/** A coach mark: the pulse and the tooltip that point once at a control.
 *  It asks for its turn HINT_ASK_MS after ``eligible`` holds and shows
 *  while it has it, ``showMs`` in all from its first showing (a drawer
 *  opened over it does not restart the clock). Being on screen once is
 *  remembered under ``seenKey``, used or not (owner, 2026-10-11: once
 *  per browser); ``done`` ends it at once, when the control is used. */
export function useCoachMark(
  who: Hint,
  eligible: boolean,
  seenKey: string,
  showMs = 12_000,
): { show: boolean; done: () => void } {
  // Seen until the browser says otherwise: nothing on the server pass,
  // nothing where storage is off.
  const [seen, setSeen] = useState(true);
  useEffect(() => {
    try { setSeen(localStorage.getItem(seenKey) === "1"); } catch { setSeen(true); }
  }, [seenKey]);
  const [over, setOver] = useState(false);
  const [asked, setAsked] = useState(false);
  const live = eligible && !seen && !over;
  useEffect(() => {
    if (!live) { setAsked(false); return undefined; }
    const id = window.setTimeout(() => setAsked(true), HINT_ASK_MS);
    return () => window.clearTimeout(id);
  }, [live]);
  const turn = useInterruption(who, live && asked);
  const upSince = useRef<number | null>(null);
  useEffect(() => {
    if (!turn) return undefined;
    if (upSince.current === null) {
      upSince.current = Date.now();
      try { localStorage.setItem(seenKey, "1"); } catch { /* storage off */ }
    }
    const left = Math.max(0, showMs - (Date.now() - upSince.current));
    const id = window.setTimeout(() => setOver(true), left);
    return () => window.clearTimeout(id);
  }, [turn, seenKey, showMs]);
  const done = useCallback(() => {
    setOver(true);
    try { localStorage.setItem(seenKey, "1"); } catch { /* storage off */ }
  }, [seenKey]);
  return { show: turn, done };
}
